import Phaser from 'phaser';
import { THEME } from '@/config/theme';
import { HIT_ZONE } from '@/utils/timing';

export interface HitZoneConfig {
  x: number;
  y: number;
  height: number;
}

export class HitZone extends Phaser.GameObjects.Container {
  private background: Phaser.GameObjects.Rectangle;
  private border: Phaser.GameObjects.Rectangle;
  private flashOverlay: Phaser.GameObjects.Rectangle;
  private backgroundHeight: number;
  private pressPulseTween: Phaser.Tweens.Tween | null = null;
  private borderPulseTween: Phaser.Tweens.Tween | null = null;
  private isPressedState: boolean = false;
  private originalX: number;

  constructor(scene: Phaser.Scene, config: HitZoneConfig) {
    super(scene, config.x, config.y);
    this.originalX = config.x;

    const width = HIT_ZONE.innerWidth;
    const height = config.height;

    // Background glow
    this.background = scene.add.rectangle(0, 0, width, height, THEME.colors.primaryHex, 0.2);
    this.backgroundHeight = height;
    this.add(this.background);

    // Border
    this.border = scene.add.rectangle(0, 0, width, height);
    this.border.setStrokeStyle(3, THEME.colors.primaryHex);
    this.border.setFillStyle(0x000000, 0);
    this.add(this.border);

    // Flash overlay for hit feedback
    this.flashOverlay = scene.add.rectangle(0, 0, width, height, 0xffffff, 0);
    this.add(this.flashOverlay);

    scene.add.existing(this);

    // Subtle pulsing animation
  }

  /**
   * Flash the hit zone for feedback
   */
  flash(color: number = THEME.colors.primaryHex, duration: number = 100): void {
    this.flashOverlay.setFillStyle(color, 0.5);

    this.scene.tweens.add({
      targets: this.flashOverlay,
      alpha: { from: 0.5, to: 0 },
      duration,
      onComplete: () => {
        this.flashOverlay.setAlpha(0);
      },
    });
  }

  /**
   * Flash green for successful hit
   */
  flashSuccess(): void {
    this.flash(THEME.colors.hit.perfectHex, 100);
  }

  /**
   * Flash yellow for good hit
   */
  flashGood(): void {
    this.flash(THEME.colors.hit.goodHex, 100);
  }

  /**
   * Flash orange for OK hit
   */
  flashOk(): void {
    this.flash(THEME.colors.hit.okHex, 100);
  }

  /**
   * Flash red for bad hit
   */
  flashBad(): void {
    this.flash(THEME.colors.hit.badHex, 150);

    // Shake effect - always return to original position
    this.scene.tweens.add({
      targets: this,
      x: this.originalX + 5,
      duration: 50,
      yoyo: true,
      repeat: 3,
      onComplete: () => {
        this.x = this.originalX;
      },
    });
  }

  /**
   * Enter the pressed-state pulse visual.
   */
  startPressPulse(): void {
    if (this.isPressedState) return;
    this.isPressedState = true;
    this.stopPressPulseTween();
    this.background.setFillStyle(THEME.colors.magentaHex, 0.18);
    this.border.setStrokeStyle(3, THEME.colors.magentaHex);
    this.pressPulseTween = this.scene.tweens.add({
      targets: this.background,
      scale: { from: 1, to: 1.04 },
      duration: 320,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.borderPulseTween = this.scene.tweens.add({
      targets: this.border,
      alpha: { from: 0.65, to: 1 },
      duration: 320,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  /**
   * Exit the pressed-state visual.
   */
  stopPressPulse(): void {
    if (!this.isPressedState) return;
    this.isPressedState = false;
    this.stopPressPulseTween();
    this.background.setScale(1);
    this.background.setFillStyle(THEME.colors.primaryHex, 0.2);
    this.border.setStrokeStyle(3, THEME.colors.primaryHex);
  }

  private stopPressPulseTween(): void {
    if (this.pressPulseTween) {
      this.pressPulseTween.stop();
      this.pressPulseTween = null;
    }
    if (this.borderPulseTween) {
      this.borderPulseTween.stop();
      this.borderPulseTween = null;
    }
  }

  /**
   * Update the hit zone appearance based on streak
   */
  updateStreak(_streak: number): void {
    // Keep hit zone visuals stable; streak UI is handled elsewhere.
    this.border.setStrokeStyle(3, THEME.colors.primaryHex);
    this.background.setFillStyle(THEME.colors.primaryHex, 0.2);
  }

  /**
   * Reset to default appearance
   */
  reset(): void {
    this.stopPressPulse();
    this.border.setStrokeStyle(3, THEME.colors.primaryHex);
    this.background.setFillStyle(THEME.colors.primaryHex, 0.2);
  }
}
